import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumb from "../../components/Common/Bredcrump";
import AutomationList from "./AutomationList";
import AutomationForm, {
  defaultAutomationForm,
  mapAutomationToForm,
} from "./AutomationForm";
import StartAutomationModal from "./StartAutomationModal";
import {
  fetchAutomations,
  fetchAutomationById,
  createAutomation,
  updateAutomation,
  deleteAutomation,
  isActiveAutomationStatus,
} from "../../services/automationService";
import { resolveOutputTableNameFromAutomation } from "../../services/automationOutputService";
import { executeManualAgent } from "../../utils/Product";
import { generateSessionId } from "../../utils/session";
import { useStreamingJson } from "../../utils/useStreamingJson";
import {
  resolveAutomationAgentId,
  resolveAutomationQuery,
  buildManualAgentStreamOptions,
} from "./automationAgentConfig";
import "./Automation.css";

const StandardAutomationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { automationId } = useParams();

  const isCreateMode = location.pathname.endsWith("/new");
  const isEditMode = Boolean(automationId) && location.pathname.endsWith("/edit");
  const isFormMode = isCreateMode || isEditMode;

  const [automations, setAutomations] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total_pages: 1,
    total_count: 0,
  });
  const [form, setForm] = useState(defaultAutomationForm());
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [startAutomationTarget, setStartAutomationTarget] = useState(null);
  const { run: runStreaming, isStreaming: isRunningAutomation } = useStreamingJson();

  const listPath = "/automation";

  const loadAutomations = useCallback(async (page = 1, perPage = 20) => {
    const { automations: list, pagination: pag } = await fetchAutomations({
      page,
      per_page: perPage,
    });

    setAutomations(list);

    if (pag) {
      setPagination({
        current_page: pag.current_page ?? page,
        per_page: pag.per_page ?? perPage,
        total_pages: pag.total_pages ?? 1,
        total_count: pag.total_count ?? list.length,
      });
    } else {
      setPagination({
        current_page: page,
        per_page: perPage,
        total_pages: 1,
        total_count: list.length,
      });
    }
    return list;
  }, []);

  const loadEditAutomation = useCallback(async () => {
    const automation = await fetchAutomationById(automationId);
    setForm(mapAutomationToForm(automation));
  }, [automationId]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!isFormMode) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        if (isCreateMode) {
          setForm(defaultAutomationForm());
        } else if (isEditMode) {
          await loadEditAutomation();
        }
      } catch (error) {
        console.error("Automation page init failed:", error);
        toast.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to load automation data"
        );
        if (isEditMode) {
          navigate(listPath);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [
    isCreateMode,
    isEditMode,
    isFormMode,
    listPath,
    loadEditAutomation,
    navigate,
  ]);

  useEffect(() => {
    if (isFormMode) return undefined;

    let cancelled = false;

    const fetchList = async () => {
      setListLoading(true);
      try {
        await loadAutomations(pagination.current_page, pagination.per_page);
      } catch (error) {
        if (!cancelled) {
          console.error("Automation list fetch failed:", error);
          toast.error(
            error.response?.data?.message ||
              error.response?.data?.error ||
              "Failed to load automations"
          );
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    };

    fetchList();
    return () => {
      cancelled = true;
    };
  }, [
    isFormMode,
    pagination.current_page,
    pagination.per_page,
    loadAutomations,
  ]);

  const hasActiveAutomations = automations.some((item) =>
    isActiveAutomationStatus(item.status)
  );

  useEffect(() => {
    if (isFormMode || !hasActiveAutomations) return undefined;

    const poll = async () => {
      try {
        await loadAutomations(pagination.current_page, pagination.per_page);
      } catch (error) {
        console.error("Automation poll failed:", error);
      }
    };

    const intervalId = setInterval(poll, 5000);
    return () => clearInterval(intervalId);
  }, [
    isFormMode,
    hasActiveAutomations,
    loadAutomations,
    pagination.current_page,
    pagination.per_page,
  ]);

  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > pagination.total_pages) return;

    setPagination((prev) => ({ ...prev, current_page: newPage }));
  };

  const handleCreate = () => {
    navigate(`${listPath}/new`);
  };

  const handleEdit = (id) => {
    navigate(`${listPath}/${id}/edit`);
  };

  const handleCancel = () => {
    navigate(listPath);
  };

  const handleSubmit = async (formData) => {
    setSaving(true);
    try {
      if (isEditMode) {
        await updateAutomation(automationId, formData);
        toast.success("Automation updated successfully");
      } else {
        await createAutomation(formData);
        toast.success("Automation created successfully");
      }
      navigate(listPath);
    } catch (error) {
      console.error("Save automation failed:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to save automation"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAutomation(id);
      toast.success("Automation deleted");
      const isLastOnPage = automations.length === 1 && pagination.current_page > 1;
      const nextPage = isLastOnPage
        ? pagination.current_page - 1
        : pagination.current_page;
      setPagination((prev) => ({ ...prev, current_page: nextPage }));
    } catch (error) {
      console.error("Delete automation failed:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to delete automation"
      );
      throw error;
    }
  };

  const handleRun = (id) => {
    const automation = automations.find((item) => String(item.id) === String(id));
    if (!automation) return;
    setStartAutomationTarget(automation);
  };

  const handleAutomationModalSubmit = async (payload) => {
    const automation = startAutomationTarget;
    if (!automation?.id || isRunningAutomation) return;

    const automationId = automation.id;
    setRunningId(automationId);

    try {
      const agentId = resolveAutomationAgentId(automation);
      if (!agentId) {
        toast.error("This automation does not have an automation agent configured.");
        return;
      }

      const outputTableName = resolveOutputTableNameFromAutomation(automation);
      const manualDefaults = buildManualAgentStreamOptions(automation, {
        outputTableName,
      });
      const query =
        payload.query?.trim() || resolveAutomationQuery(automation);

      await runStreaming(async (onChunk) =>
        executeManualAgent(agentId, generateSessionId(), query, onChunk, {}, {
          variables: payload.variables ?? manualDefaults.variables,
          filters: manualDefaults.filters,
          metadata: manualDefaults.metadata,
          outputTableName,
          automationId,
          source: "manual run",
          files: payload.files ?? [],
        })
      );

      toast.success("Manual automation run started");
      setStartAutomationTarget(null);
      if (!isFormMode) {
        await loadAutomations(pagination.current_page, pagination.per_page);
      }
    } catch (error) {
      console.error("Automation action failed:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to start automation"
      );
    } finally {
      setRunningId(null);
    }
  };

  if (loading) {
    return (
      <div className="automation-page">
        {!isFormMode && <Breadcrumb />}
        <div className="automation-loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      {!isFormMode && <Breadcrumb />}
      <StartAutomationModal
        show={Boolean(startAutomationTarget)}
        onHide={() => {
          if (!isRunningAutomation) setStartAutomationTarget(null);
        }}
        automation={startAutomationTarget}
        isRunning={isRunningAutomation}
        onSubmit={handleAutomationModalSubmit}
      />
      {isFormMode ? (
        <AutomationForm
          form={form}
          setForm={setForm}
          isEdit={isEditMode}
          saving={saving}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      ) : (
        <div className="automation-page">
          <AutomationList
            automations={automations}
            loading={listLoading}
            pagination={pagination}
            onPageChange={handlePageChange}
            runningId={runningId}
            onCreate={handleCreate}
            onEdit={handleEdit}
            onRun={handleRun}
            onDelete={handleDelete}
          />
        </div>
      )}
    </>
  );
};

export default StandardAutomationPage;
